// covered-by: frontends/carrie/src/components/ask-puntt-review/__tests__/reviewCommentDisplay.test.ts
import { type AskPunttReviewComment } from "@mg/schemas/src/christo/askPunttReview";

import {
  extractRiskSection,
  parseCommentSections,
  slugifyCommentSectionLabel,
  type CommentSection,
} from "../../routes/ask-puntt/utils/parseCommentSections";

function stripMarkdownInline(text: string): string {
  return text.replace(/\*\*/g, "").replace(/`/g, "").trim();
}

function firstLine(text: string): string {
  return text.trim().split("\n")[0]?.trim() ?? "";
}

export function reviewCommentTitle(comment: AskPunttReviewComment): string {
  if (comment.rule?.trim()) return comment.rule.trim();

  const parsed = parseCommentSections(comment.description);
  const { risk } = extractRiskSection(parsed);
  if (risk?.body.trim()) {
    return stripMarkdownInline(firstLine(risk.body));
  }

  const issue = parsed.find(
    (section) => slugifyCommentSectionLabel(section.label) === "issue",
  );
  if (issue?.body.trim()) {
    const lead = firstLine(issue.body);
    return stripMarkdownInline(lead) || "Finding";
  }

  const first = firstLine(comment.description);
  if (!first) return "Finding";
  return stripMarkdownInline(first.replace(/^\*\*[^*]+:\*\*\s*/, ""));
}

export function reviewCommentCollapsedSections(
  description: string,
): CommentSection[] {
  const parsed = parseCommentSections(description);
  const { sections } = extractRiskSection(parsed);
  return sections.filter((section) => {
    const slug = slugifyCommentSectionLabel(section.label);
    return slug === "issue" || slug === "fix";
  });
}

export function formatReviewCommentTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

export type ReviewCommentAuthorOptions = {
  aiLabel: string;
  currentUserId?: string;
  currentUserName?: string;
  authorNameByUserId?: Readonly<Record<string, string>>;
  unknownAuthorLabel: string;
};

export function reviewCommentAuthorLabel(
  comment: AskPunttReviewComment,
  options: ReviewCommentAuthorOptions,
): string {
  const isAiComment = comment.isAI || Boolean(comment.aiPersona?.name?.trim());
  if (isAiComment) return options.aiLabel;

  const createdBy = comment.createdBy?.trim();
  if (createdBy && options.authorNameByUserId?.[createdBy]?.trim()) {
    return options.authorNameByUserId[createdBy].trim();
  }
  if (
    createdBy &&
    options.currentUserId &&
    createdBy === options.currentUserId &&
    options.currentUserName?.trim()
  ) {
    return options.currentUserName.trim();
  }

  return options.unknownAuthorLabel;
}

export function buildCommentNumberById(
  comments: readonly AskPunttReviewComment[],
): Record<string, number> {
  const map: Record<string, number> = {};
  comments
    .filter((comment) => !comment.isDeleted)
    .forEach((comment, index) => {
      map[comment._id] = index + 1;
    });
  return map;
}
